import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import useIsMobile from '../../hooks/useIsMobile';
import { useLandingLanguage } from '../../context/LandingLanguageContext';

/** Google Apps Script web app (waitlist) — hardcoded temporarily */
const SHEETS_SCRIPT_URL =
  'https://script.google.com/macros/s/AKfycbyL6k-dTH_CpDeNxAjlJ1A5emjlUdi6yIwPNchX5sOEEWdPVDqCtRfS4tTuTA6WFuBN1w/exec';

const WaitlistSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isMobile = useIsMobile();
  const { t } = useLandingLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError(t('waitlist.errInvalid'));
      return;
    }
    setError('');
    setLoading(true);
    let sent = false;
    try {
      const createdAt = new Date().toISOString();
      // POST with text/plain avoids CORS preflight and redirect issues with Apps Script
      await fetch(SHEETS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ email: trimmed, created_at: createdAt }),
      });
      sent = true;
    } catch (err) {
      console.error('Waitlist email was not sent to sheet.', {
        email: trimmed,
        error: err instanceof Error ? err.message : err,
      });
      setError(t('waitlist.errGeneric'));
    }
    setLoading(false);
    if (sent) {
      setSubmitted(true);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      ref={ref}
      style={{
        background: '#0F0F0F',
        padding: 'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Glow */}
      <div style={{ position: 'absolute', bottom: '-100px', right: '15%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 65%)', filter: 'blur(60px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '40px' : 'clamp(40px, 6vw, 100px)', alignItems: 'center' }}>

        {/* LEFT */}
        <motion.div initial={{ opacity: 0, x: -24 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} style={{ textAlign: isMobile ? 'center' : 'left' }}>
          <h2 style={{ color: '#F5F1EA', fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: '900', letterSpacing: '-2.5px', lineHeight: 1.02, margin: '0 0 20px', fontFamily: 'Inter, sans-serif' }}>
            {t('waitlist.title1')}<br />
            <span style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 50%, #D4AF37 100%)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              {t('waitlist.title2')}
            </span>
          </h2>
          <p style={{ color: '#6B7280', fontSize: '16px', lineHeight: 1.7, margin: isMobile ? '0 auto 40px' : '0 0 40px', maxWidth: '380px' }}>
            {t('waitlist.body')}
          </p>

        </motion.div>

        {/* RIGHT — glowing form card */}
        <motion.div initial={{ opacity: 0, x: 24 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}>
          <div style={{
            background: 'linear-gradient(145deg, rgba(212,175,55,0.06) 0%, rgba(255,255,255,0.02) 100%)',
            border: '1px solid rgba(212,175,55,0.15)',
            borderRadius: '28px',
            padding: 'clamp(28px, 4vw, 44px)',
            boxShadow: '0 0 80px rgba(212,175,55,0.06), 0 32px 64px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Top shimmer line */}
            <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.6), transparent)' }} />

            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #D4AF37, #B8962E)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 32px rgba(212,175,55,0.4)' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L20 7" stroke="#0F0F0F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <p style={{ color: '#F5F1EA', fontSize: '18px', fontWeight: '800', margin: '0 0 8px' }}>{t('waitlist.successTitle')}</p>
                  <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>{t('waitlist.successBody')}</p>
                </motion.div>
              ) : (
                <motion.form key="form" onSubmit={handleSubmit}>
                  <p style={{ color: '#F5F1EA', fontSize: '18px', fontWeight: '800', margin: '0 0 6px', letterSpacing: '-0.4px' }}>{t('waitlist.formTitle')}</p>
                  <p style={{ color: '#6B7280', fontSize: '13px', margin: '0 0 24px' }}>{t('waitlist.formSubtitle')}</p>

                  <label style={{ display: 'block', color: '#8B93A7', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>{t('waitlist.emailLabel')}</label>
                  <input
                    type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder={t('waitlist.emailPlaceholder')}
                    style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', border: `1px solid ${error ? 'rgba(220,38,38,0.5)' : 'rgba(255,255,255,0.09)'}`, borderRadius: '14px', padding: '14px 16px', color: '#F5F1EA', fontSize: '15px', fontFamily: 'Inter, sans-serif', outline: 'none', marginBottom: error ? '8px' : '16px' }}
                    onFocus={e => e.target.style.borderColor = 'rgba(212,175,55,0.4)'}
                    onBlur={e => e.target.style.borderColor = error ? 'rgba(220,38,38,0.5)' : 'rgba(255,255,255,0.09)'}
                  />
                  {error && <p style={{ color: '#EF4444', fontSize: '12px', margin: '0 0 12px' }}>{error}</p>}

                  <motion.button type="submit" disabled={loading} whileHover={{ scale: 1.02, boxShadow: '0 8px 32px rgba(212,175,55,0.45)' }} whileTap={{ scale: 0.98 }}
                    style={{ width: '100%', background: 'linear-gradient(135deg, #D4AF37, #C49A28)', color: '#0F0F0F', border: 'none', borderRadius: '14px', padding: '15px', fontSize: '14px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 4px 24px rgba(212,175,55,0.25)', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.2px' }}>
                    {loading ? t('waitlist.joining') : t('waitlist.cta')}
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WaitlistSection;
